import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardText, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Small action button shown on VERIFIED sale rows that don't yet have a
 * registration started. Useful for sales that left a small `due` balance
 * (which blocks the auto-start gate inside POST /sales/{id}/approve) but
 * are operationally ready to enter the post-sale registration pipeline.
 *
 * Owners (admins / users with `registrations.edit`) see a button that
 * triggers POST /api/registrations/start-for-sale/{sale_id} (idempotent —
 * returns the existing registration if one already exists).
 *
 * If the sale ALREADY has a registration_id stamped on it, render a link
 * to the Registrations page instead.
 */
export default function StartRegistrationButton({ sale, user, onStarted }) {
  const [busy, setBusy] = useState(false);

  if (!sale || sale.approval_status !== "approved") return null;
  const isAdmin = user?.role === "admin";
  const canEdit = isAdmin || (user?.permissions || []).includes("registrations.manage");
  if (!canEdit) return null;

  // If a registration is already linked on the sale, we expose a "View"
  // shortcut instead of the start button.
  if (sale.registration_id) {
    return (
      <Link
        to="/registrations"
        title="Open registration pipeline"
        data-testid={`sale-view-reg-${sale.id}`}
        className="p-1.5 border border-canvas-line hover:border-ink inline-flex items-center"
      >
        <ArrowSquareOut size={13} weight="bold" />
      </Link>
    );
  }

  const start = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/start-for-sale/${sale.id}`);
      toast.success(`Registration started for ${data.customer_name || "sale"}`);
      onStarted?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={start}
      disabled={busy}
      title={busy ? "Starting…" : "Start registration pipeline for this sale"}
      data-testid={`sale-start-reg-${sale.id}`}
      className="p-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white inline-flex items-center justify-center disabled:opacity-60"
    >
      <ClipboardText size={13} weight="bold" />
    </button>
  );
}
